import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { formatDate, cn } from '../../lib/utils';
import { 
  User, Phone, Mail, Home, Calendar, Shield, Upload, 
  FileText, AlertTriangle, CheckCircle, Clock, Edit, Save, X
} from 'lucide-react';
import { toast } from 'sonner';

// Required compliance documents for NDIS workers
const COMPLIANCE_DOCUMENTS = [
  { type: 'ndis_worker_screening', label: 'NDIS Worker Screening Check', required: true },
  { type: 'police_check', label: 'National Police Check', required: true },
  { type: 'wwcc', label: 'Working with Children Check', required: true },
  { type: 'first_aid', label: 'First Aid Certificate', required: true },
  { type: 'cpr', label: 'CPR Certificate', required: true },
  { type: 'manual_handling', label: 'Manual Handling Certificate', required: false },
  { type: 'medication_competency', label: 'Medication Competency', required: false },
  { type: 'drivers_license', label: "Driver's License", required: false },
  { type: 'car_insurance', label: 'Car Insurance', required: false },
  { type: 'car_registration', label: 'Car Registration', required: false },
  { type: 'qualifications', label: 'Qualifications/Certificates', required: false },
  { type: 'other', label: 'Other Documents', required: false },
];

const WorkerProfilePage = () => {
  const { api, user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [complianceDocs, setComplianceDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  const [newDocument, setNewDocument] = useState({
    document_type: '',
    issued_date: '',
    expiry_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/workers/${user.id}`);
      setProfile(response.data);
      setEditedProfile(response.data);
      setComplianceDocs(response.data.compliance_documents || []);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      await api.put(`/workers/${user.id}`, {
        phone: editedProfile.phone,
        address: editedProfile.address,
        emergency_contact_name: editedProfile.emergency_contact_name,
        emergency_contact_phone: editedProfile.emergency_contact_phone,
        skills: editedProfile.skills || []
      });
      toast.success('Profile updated successfully');
      setIsEditing(false);
      fetchProfile();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload a PDF or image file (JPG, PNG)');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUploadDocument = async () => {
    if (!newDocument.document_type) {
      toast.error('Please select a document type');
      return;
    }
    
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_type', newDocument.document_type);
      if (newDocument.issued_date) formData.append('issued_date', newDocument.issued_date);
      if (newDocument.expiry_date) formData.append('expiry_date', newDocument.expiry_date);
      if (newDocument.notes) formData.append('notes', newDocument.notes);

      await api.post(`/workers/${user.id}/compliance-documents-upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast.success('Document uploaded successfully! Pending admin approval.');
      setUploadDialogOpen(false);
      setNewDocument({
        document_type: '',
        issued_date: '',
        expiry_date: '',
        notes: ''
      });
      setSelectedFile(null);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadLoading(false);
    }
  };

  const getDocumentStatus = (docType) => {
    const doc = complianceDocs.find(d => d.document_type === docType);
    if (!doc) return { status: 'missing', label: 'Missing', color: 'bg-gray-500' };
    
    if (doc.status === 'pending_review') {
      return { status: 'pending', label: 'Pending Review', color: 'bg-amber-500' };
    }
    
    if (doc.expiry_date) {
      const expiry = new Date(doc.expiry_date);
      const now = new Date();
      const daysUntilExpiry = Math.floor((expiry - now) / (1000 * 60 * 60 * 24));
      
      if (daysUntilExpiry < 0) {
        return { status: 'expired', label: 'Expired', color: 'bg-red-500' };
      } else if (daysUntilExpiry <= 30) {
        return { status: 'expiring', label: `Expires in ${daysUntilExpiry} days`, color: 'bg-amber-500' };
      }
    }
    
    if (doc.status === 'approved') {
      return { status: 'valid', label: 'Valid', color: 'bg-green-500' };
    }
    
    return { status: 'pending', label: 'Pending', color: 'bg-amber-500' };
  };

  const calculateComplianceScore = () => {
    const requiredDocs = COMPLIANCE_DOCUMENTS.filter(d => d.required);
    const validDocs = requiredDocs.filter(d => {
      const status = getDocumentStatus(d.type);
      return status.status === 'valid';
    });
    return Math.round((validDocs.length / requiredDocs.length) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading profile...</div>
      </div>
    );
  }

  const complianceScore = calculateComplianceScore();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="worker-profile-page">
      <PageHeader 
        title="My Profile"
        description="Manage your details and compliance documents"
      />

      {/* Compliance Status Banner */}
      <Card className={cn(
        "border-2",
        complianceScore === 100 ? "border-green-500/30 bg-green-500/5" : "border-amber-500/30 bg-amber-500/5"
      )}>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={cn(
                "p-3 rounded-full",
                complianceScore === 100 ? "bg-green-500/20" : "bg-amber-500/20"
              )}>
                <Shield className={cn(
                  "h-6 w-6",
                  complianceScore === 100 ? "text-green-500" : "text-amber-500"
                )} />
              </div>
              <div>
                <h3 className="font-semibold">
                  {complianceScore === 100 ? 'Fully Compliant' : 'Compliance Action Required'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {complianceScore === 100 
                    ? 'All required documents are valid and up to date'
                    : 'Some compliance documents need attention'
                  }
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">{complianceScore}%</div>
              <Progress value={complianceScore} className="w-32 h-2 mt-1" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="details" className="space-y-6">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="details">Personal Details</TabsTrigger>
          <TabsTrigger value="compliance">Compliance Documents</TabsTrigger>
        </TabsList>

        {/* Personal Details Tab */}
        <TabsContent value="details" className="space-y-6">
          <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Personal Information</CardTitle>
                <CardDescription>Your contact details and emergency contact</CardDescription>
              </div>
              {!isEditing ? (
                <Button variant="outline" onClick={() => setIsEditing(true)} data-testid="edit-profile-btn">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => {
                    setIsEditing(false);
                    setEditedProfile(profile);
                  }}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button onClick={handleSaveProfile} data-testid="save-profile-btn">
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Basic Info - Read Only */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Full Name</Label>
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/30">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{profile?.first_name} {profile?.last_name}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Email</Label>
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/30">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{profile?.email}</span>
                  </div>
                </div>
              </div>

              {/* Editable Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Phone Number</Label>
                  {isEditing ? (
                    <Input 
                      value={editedProfile?.phone || ''}
                      onChange={(e) => setEditedProfile({...editedProfile, phone: e.target.value})}
                      placeholder="Enter phone number"
                      data-testid="phone-input"
                    />
                  ) : (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/30">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{profile?.phone || 'Not provided'}</span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  {isEditing ? (
                    <Input 
                      value={editedProfile?.address || ''}
                      onChange={(e) => setEditedProfile({...editedProfile, address: e.target.value})}
                      placeholder="Enter address"
                    />
                  ) : (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/30">
                      <Home className="h-4 w-4 text-muted-foreground" />
                      <span>{profile?.address || 'Not provided'}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="pt-4 border-t border-border/40">
                <h3 className="font-semibold mb-4">Emergency Contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Contact Name</Label>
                    {isEditing ? (
                      <Input 
                        value={editedProfile?.emergency_contact_name || ''}
                        onChange={(e) => setEditedProfile({...editedProfile, emergency_contact_name: e.target.value})}
                        placeholder="Emergency contact name"
                      />
                    ) : (
                      <div className="p-3 rounded-lg bg-muted/30">
                        <span>{profile?.emergency_contact_name || 'Not provided'}</span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Phone</Label>
                    {isEditing ? (
                      <Input 
                        value={editedProfile?.emergency_contact_phone || ''}
                        onChange={(e) => setEditedProfile({...editedProfile, emergency_contact_phone: e.target.value})}
                        placeholder="Emergency contact phone"
                      />
                    ) : (
                      <div className="p-3 rounded-lg bg-muted/30">
                        <span>{profile?.emergency_contact_phone || 'Not provided'}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Role & Assignments - Read Only */}
              <div className="pt-4 border-t border-border/40">
                <h3 className="font-semibold mb-4">Role & Assignments</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Role</Label>
                    <div className="p-3 rounded-lg bg-muted/30">
                      <Badge variant="outline" className="capitalize">{profile?.role}</Badge>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Assigned Houses</Label>
                    <div className="p-3 rounded-lg bg-muted/30 flex flex-wrap gap-2">
                      {profile?.assigned_houses?.length > 0 ? (
                        profile.assigned_houses.map((house, idx) => (
                          <Badge key={idx} variant="secondary">{house}</Badge>
                        ))
                      ) : (
                        <span className="text-muted-foreground">None assigned</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compliance Documents Tab */}
        <TabsContent value="compliance" className="space-y-6">
          <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Compliance Documents</CardTitle>
                <CardDescription>Upload and manage your required compliance documents</CardDescription>
              </div>
              <Dialog open={uploadDialogOpen} onOpenChange={(open) => {
                if (!open) {
                  setSelectedFile(null);
                  setNewDocument({ document_type: '', issued_date: '', expiry_date: '', notes: '' });
                }
                setUploadDialogOpen(open);
              }}>
                <DialogTrigger asChild>
                  <Button data-testid="upload-doc-btn">
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Document
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Upload Compliance Document</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Document Type *</Label>
                      <Select 
                        value={newDocument.document_type} 
                        onValueChange={(v) => setNewDocument({...newDocument, document_type: v})}
                      >
                        <SelectTrigger data-testid="doc-type-select">
                          <SelectValue placeholder="Select document type" />
                        </SelectTrigger>
                        <SelectContent>
                          {COMPLIANCE_DOCUMENTS.map(doc => (
                            <SelectItem key={doc.type} value={doc.type}>
                              {doc.label} {doc.required && '*'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Upload File *</Label>
                      <div className="flex items-center gap-2">
                        <Input 
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleFileSelect}
                          className="cursor-pointer"
                          data-testid="doc-file-input"
                        />
                      </div>
                      {selectedFile && (
                        <p className="text-sm text-muted-foreground">
                          Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Accepted formats: PDF, JPG, PNG (max 10MB)
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Issue Date</Label>
                        <Input 
                          type="date"
                          value={newDocument.issued_date}
                          onChange={(e) => setNewDocument({...newDocument, issued_date: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Expiry Date</Label>
                        <Input 
                          type="date"
                          value={newDocument.expiry_date}
                          onChange={(e) => setNewDocument({...newDocument, expiry_date: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Textarea 
                        placeholder="Any additional notes (e.g., certificate number)..."
                        value={newDocument.notes}
                        onChange={(e) => setNewDocument({...newDocument, notes: e.target.value})}
                        rows={2}
                      />
                    </div>
                    
                    <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm">
                      <p className="text-amber-600 dark:text-amber-400">
                        ⏳ Documents will be reviewed and approved by an administrator.
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setUploadDialogOpen(false)} disabled={uploadLoading}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleUploadDocument} 
                      disabled={uploadLoading || !selectedFile || !newDocument.document_type}
                      data-testid="submit-doc-btn"
                    >
                      {uploadLoading ? 'Uploading...' : 'Upload Document'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {/* Required Documents */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Required Documents
                </h3>
                <div className="space-y-3">
                  {COMPLIANCE_DOCUMENTS.filter(d => d.required).map(doc => {
                    const status = getDocumentStatus(doc.type);
                    const existingDoc = complianceDocs.find(d => d.document_type === doc.type);
                    
                    return (
                      <div 
                        key={doc.type}
                        className="flex items-center justify-between p-4 rounded-lg border border-border/40 bg-background/50"
                      >
                        <div className="flex items-center gap-3">
                          <div className={cn("p-2 rounded-full", 
                            status.status === 'valid' ? 'bg-green-500/20' :
                            status.status === 'expired' ? 'bg-red-500/20' :
                            status.status === 'missing' ? 'bg-gray-500/20' : 'bg-amber-500/20'
                          )}>
                            {status.status === 'valid' ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : status.status === 'expired' ? (
                              <AlertTriangle className="h-4 w-4 text-red-500" />
                            ) : status.status === 'missing' ? (
                              <FileText className="h-4 w-4 text-gray-500" />
                            ) : (
                              <Clock className="h-4 w-4 text-amber-500" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{doc.label}</p>
                            {existingDoc && (
                              <p className="text-xs text-muted-foreground">
                                {existingDoc.document_name}
                                {existingDoc.expiry_date && ` • Expires: ${formatDate(existingDoc.expiry_date)}`}
                              </p>
                            )}
                          </div>
                        </div>
                        <Badge className={cn(status.color, "text-white")}>
                          {status.label}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Optional Documents */}
              <div className="space-y-4 mt-8">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Optional Documents
                </h3>
                <div className="space-y-3">
                  {COMPLIANCE_DOCUMENTS.filter(d => !d.required).map(doc => {
                    const status = getDocumentStatus(doc.type);
                    const existingDoc = complianceDocs.find(d => d.document_type === doc.type);
                    
                    return (
                      <div 
                        key={doc.type}
                        className="flex items-center justify-between p-4 rounded-lg border border-border/40 bg-background/50"
                      >
                        <div className="flex items-center gap-3">
                          <div className={cn("p-2 rounded-full", 
                            status.status === 'valid' ? 'bg-green-500/20' :
                            status.status === 'missing' ? 'bg-gray-500/20' : 'bg-amber-500/20'
                          )}>
                            {status.status === 'valid' ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : status.status === 'missing' ? (
                              <FileText className="h-4 w-4 text-gray-500" />
                            ) : (
                              <Clock className="h-4 w-4 text-amber-500" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{doc.label}</p>
                            {existingDoc && (
                              <p className="text-xs text-muted-foreground">
                                {existingDoc.document_name}
                                {existingDoc.expiry_date && ` • Expires: ${formatDate(existingDoc.expiry_date)}`}
                              </p>
                            )}
                          </div>
                        </div>
                        {status.status !== 'missing' ? (
                          <Badge className={cn(status.color, "text-white")}>
                            {status.label}
                          </Badge>
                        ) : (
                          <span className="text-sm text-muted-foreground">Not uploaded</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WorkerProfilePage;
